import { useMock, corporateGet, corporatePost, corporatePut, corporateDelete } from './client';
import rolesMock from '@/mocks/corporate/roles';
import organizationalUnitsApi from './organizationalUnits';

const SCHEMA_GAP =
    'Mutação de cargo via /corporate/roles ainda não está backendizada. Use Estrutura organizacional ou o modo mock.';

function schemaGapError() {
    const error = new Error(SCHEMA_GAP);
    error.code = 'SCHEMA_GAP';
    return error;
}

function normalizeStatus(status) {
    if (status === 'active' || status === 'inactive') return status;
    if (status === 1 || status === '1' || status === true) return 'active';
    if (status === 0 || status === '0' || status === false) return 'inactive';
    return status ? 'active' : 'inactive';
}

/**
 * Normaliza cargo do schema `/corporate/roles` (e fallback organizational-units type=role).
 * Garante `sectorId` para atrelar ao colaborador no create/edit.
 */
export function normalizeRole(role = {}) {
    if (!role || typeof role !== 'object') return role;
    const sectorId = role.sectorId || role.parentId || null;
    return {
        ...role,
        id: role.id,
        name: role.name || '',
        description: role.description || '',
        status: normalizeStatus(role.status),
        type: role.type || 'role',
        sectorId,
        parentId: role.parentId || sectorId,
        sectorName: role.sectorName || '',
        courseIds: Array.isArray(role.courseIds) ? role.courseIds : [],
        collaborators: Array.isArray(role.collaborators) ? role.collaborators : [],
        collaboratorsCount: Number(
            role.collaboratorsCount
            ?? (Array.isArray(role.collaborators) ? role.collaborators.length : 0),
        ) || 0,
    };
}

function normalizeListResponse(res = {}) {
    const content = Array.isArray(res.content)
        ? res.content.map(normalizeRole)
        : [];
    return {
        ...res,
        content,
        totalElements: Number(res.totalElements ?? content.length) || 0,
        page: Number(res.page ?? 0) || 0,
        size: Number(res.size ?? content.length) || 0,
    };
}

async function listFromOrganizationalUnits(accessToken, params = {}) {
    const res = await organizationalUnitsApi.list(accessToken, {
        type: 'role',
        status: params.status,
        parentId: params.sectorId || undefined,
        search: params.search || undefined,
    });
    return normalizeListResponse({
        content: (res.content || []).map((unit) => ({
            ...unit,
            sectorId: unit.parentId,
            courseIds: [],
            collaborators: [],
            collaboratorsCount: 0,
        })),
        totalElements: res.totalElements ?? (res.content || []).length,
        page: res.page ?? 0,
        size: res.size ?? (res.content || []).length,
    });
}

export default {
    /**
     * GET /corporate/roles
     * Query: sectorId, status, search
     */
    async list(accessToken, params = {}) {
        if (useMock()) {
            const res = await rolesMock.list(params);
            return normalizeListResponse(res);
        }

        const query = {
            sectorId: params.sectorId || undefined,
            status: params.status || undefined,
            search: params.search || undefined,
            page: params.page != null ? params.page : undefined,
            size: params.size != null ? params.size : undefined,
        };

        try {
            const res = await corporateGet('/corporate/roles', accessToken, query);
            return normalizeListResponse(res);
        } catch (e) {
            console.warn('[corporate/roles] fallback organizational-units type=role', e);
            return listFromOrganizationalUnits(accessToken, params);
        }
    },

    /**
     * GET /corporate/roles/{id}
     */
    async getById(accessToken, id) {
        if (useMock()) return normalizeRole(await rolesMock.getById(id));
        try {
            return normalizeRole(await corporateGet(`/corporate/roles/${id}`, accessToken));
        } catch (e) {
            const unit = await organizationalUnitsApi.getById(accessToken, id);
            return normalizeRole({
                ...unit,
                sectorId: unit.parentId,
            });
        }
    },

    async getStats(accessToken, id) {
        if (useMock()) {
            const role = await rolesMock.getById(id);
            return {
                id: role?.id,
                type: 'role',
                name: role?.name,
                status: role?.status || 'active',
                sectorId: role?.sectorId,
                sectorName: role?.sectorName,
                collaboratorsCount: Array.isArray(role?.collaborators) ? role.collaborators.length : 0,
                rolesCount: 0,
                coursesCount: Array.isArray(role?.courseIds) ? role.courseIds.length : 0,
                completionAvg: 0,
                overdueTotal: 0,
                courses: [],
                roles: [],
                courseIds: role?.courseIds || [],
            };
        }
        return corporateGet(`/corporate/roles/${id}/stats`, accessToken);
    },

    async create(accessToken, payload) {
        if (useMock()) return normalizeRole(await rolesMock.create(payload));
        return organizationalUnitsApi.create(accessToken, {
            type: 'role',
            parentId: payload.sectorId || payload.parentId,
            name: payload.name,
            description: payload.description,
            status: payload.status || 'active',
        });
    },

    async update(accessToken, id, payload) {
        if (useMock()) return normalizeRole(await rolesMock.update(id, payload));
        return organizationalUnitsApi.update(accessToken, id, {
            name: payload.name,
            description: payload.description,
            status: payload.status,
            parentId: payload.sectorId || payload.parentId,
        });
    },

    async remove(accessToken, id) {
        if (useMock()) return rolesMock.remove(id);
        return organizationalUnitsApi.remove(accessToken, id);
    },

    async duplicate(accessToken, id) {
        if (useMock()) return rolesMock.duplicate(id);
        throw schemaGapError();
    },
};
